import { Component, Inject, ViewChild } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { HttpParams } from '@angular/common/http';
import { PTicketFilterComponent } from '../../pTicket/p-ticket-filter/p-ticket-filter.component';
import { ReportingGraphComponent } from '../reporting-graph/reporting-graph.component';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { ChartType } from 'chart.js';
import { pTicket } from '../../../interfaces/pTicket';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { Filter } from '../../../interfaces/filter';
import { Filters } from '../../../mock-data/mock-filters';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { OverlayModule } from '@angular/cdk/overlay';
import { ReportService } from '../../../services/report.service';
import { UserService } from '../../../services/user.service';
import { ActiveFilter } from '../../../interfaces/active-filter';
import { report, xAxisOption } from '../../../interfaces/preport';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-new-report-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    PTicketFilterComponent,
    ReportingGraphComponent,
    FormsModule,
    NgFor,
    NgIf,
    MatButtonModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatChipsModule,
    OverlayModule,
    DatePipe,
    MatMenuModule
  ],
  templateUrl: './new-report-dialog.component.html',
  styleUrl: './new-report-dialog.component.scss',
})
export class NewReportDialogComponent {
  report: report;
  selectedXAxis: keyof pTicket = 'pct_has_solution';
  filterOpen = false;
  isNavigatingToList = false;

  xAxisOptions: xAxisOption[] = [
    { key: 'pct_has_solution', label: 'Has Solution' },
    { key: 'pct_supportlevel', label: 'Support Level' },
    { key: 'pct_status', label: 'Status' },
    { key: 'pct_tickettype', label: 'Ticket Type' },
    { key: 'pct_company', label: 'Company' },
    { key: 'pct_countrycode', label: 'Country' },
    { key: 'pct_issuetype', label: 'Issue Type' },
    { key: 'pct_severity', label: 'Severity' },
    { key: 'pct_component', label: 'Component' },
    { key: 'pct_status_granulated', label: 'Granulated status' },
    { key: 'pkeywords', label: 'Keywords' },
    { key: 'pct_product', label: 'Product' },
  ];

  editingTitle = false;
  isSavingChanges: boolean = false;
  @ViewChild(ReportingGraphComponent)
  reportingGraphComponent!: ReportingGraphComponent;

  constructor(
    public dialogRef: MatDialogRef<NewReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { report?: report; id: number },
    private reportService: ReportService,
    private userService: UserService,
    private router: Router,
  ) {
    this.report = data.report
      ? { ...data.report }
      : {
          id: -1,
          rep_title: 'New Report',
          rep_chartType: 'bar',
          rep_xAxisOption: this.xAxisOptions[0],
          rep_filter: new HttpParams(),
        };
  }

  ngOnInit(): void {
    if (this.data.report) {
      this.report = this.data.report;
    }
    this.activeFilters = [];
    this.loadActiveFilters(this.report.rep_filter);
    this.activeFilters = [...this.activeFilters];
    this.reportingGraphComponent.loadTickets(this.report);
  }

  onXAxisChange(newXAxis: string) {
    const selectedKey = newXAxis as keyof pTicket;
    this.selectedXAxis = selectedKey;
    this.report.rep_xAxisOption = {
      key: selectedKey,
      label:
        this.xAxisOptions.find((o) => o.key === selectedKey)?.label ||
        selectedKey,
    };
    this.reportingGraphComponent.loadTickets(this.report);
  }

  onTypeChange(newType: ChartType) {
    this.report.rep_chartType = newType;
    this.reportingGraphComponent.loadTickets(this.report);
  }

  onFilterChange(filter: HttpParams = new HttpParams()): void {
    this.report.rep_filter = filter;
    this.loadActiveFilters(filter);
    this.reportingGraphComponent.loadTickets(this.report);
  }

  loadActiveFilters(filters: HttpParams): void {
    this.activeFilters = [];

    const gte = filters.get('updatedAt[$gte]');
    const lte = filters.get('updatedAt[$lte]');
    if (gte && lte) {
      const dateRangeFilterDef = Filters.find(
        (f) => f.filterType === 'dateRange',
      );
      if (dateRangeFilterDef) {
        this.activeFilters.push({
          key: dateRangeFilterDef,
          value: { start: gte, end: lte } as any,
        });
      } else {
        console.warn('No dateRange filter definition found in Filters');
      }
    }

    filters.keys().forEach((key) => {
      if (key === 'updatedAt[$gte]' || key === 'updatedAt[$lte]') return; // already handled above

      const filterDef = Filters.find((f) => f.db_key === key);
      if (!filterDef) {
        console.warn(`Unknown filter key: ${key}`);
        return;
      }

      const values = filters.getAll(key);
      values?.forEach((value) => {
        this.activeFilters.push({ key: filterDef, value });
      });
    });

    this.activeFilters = [...this.activeFilters];
  }

  private buildHttpParamsFromActiveFilters(
    filters: { key: Filter; value: any }[],
  ): HttpParams {
    let params = new HttpParams();
    filters.forEach((f) => {
      if (f.key.filterType === 'dateRange') {
        const start = f.value?.start;
        const end = f.value?.end;
        if (start && end) {
          params = params
            .append('updatedAt[$gte]', new Date(start).toISOString())
            .append('updatedAt[$lte]', new Date(end).toISOString());
        }
        return;
      }
      if (f.value !== null && f.value !== undefined && f.value !== '') {
        params = params.append(f.key.db_key, f.value);
      }
    });
    return params;
  }

  activeFilters: { key: Filter; value: any }[] = [];

  removeActiveFilterChip(chip: { key: Filter; value: any }): void {
    const updated = this.activeFilters.filter((f) => {
      if (
        f.key.filterType === 'dateRange' &&
        chip.key.filterType === 'dateRange'
      ) {
        return false; // there's only ever one date-range filter, so removing it clears it entirely
      }
      return !(f.key.db_key === chip.key.db_key && f.value === chip.value);
    });
    const params = this.buildHttpParamsFromActiveFilters(updated);
    this.onFilterChange(params);
  }

  /** Converts the report's raw HttpParams into ActiveFilter[] usable by the list's saved-filter format. */
  private buildActiveFiltersFromParams(params: HttpParams): ActiveFilter[] {
    const result: ActiveFilter[] = [];

    const gte = params.get('updatedAt[$gte]');
    const lte = params.get('updatedAt[$lte]');
    if (gte && lte) {
      const dateRangeFilterDef = Filters.find(
        (f) => f.filterType === 'dateRange',
      );
      if (dateRangeFilterDef) {
        result.push({
          id: crypto.randomUUID(),
          key: dateRangeFilterDef,
          value: { start: gte, end: lte },
        });
      }
    }

    params.keys().forEach((key) => {
      if (key === 'updatedAt[$gte]' || key === 'updatedAt[$lte]') return;
      const filterDef = Filters.find((f) => f.db_key === key);
      if (!filterDef) return;
      params.getAll(key)?.forEach((value) => {
        result.push({ id: crypto.randomUUID(), key: filterDef, value });
      });
    });

    return result;
  }

  /**
   * User clicked a chart segment. Merge the report's existing filter with the
   * clicked field/value (overriding any existing filter on that same field),
   * save it as the saved filter set for BOTH tCC and pCC tabs, then navigate
   * to the ticket list so it picks the saved filters up on load.
   */
  onSegmentClicked(event: { field: string; value: string }): void {
    const filterDef = Filters.find((f) => f.db_key === event.field);
    if (!filterDef) {
      console.warn(`No filter definition found for field: ${event.field}`);
      return;
    }

    const baseFilters = this.buildActiveFiltersFromParams(
      this.report.rep_filter,
    );
    const merged = baseFilters.filter((f) => f.key?.db_key !== event.field);
    merged.push({
      id: crypto.randomUUID(),
      key: filterDef,
      value: event.value,
    });

    this.isNavigatingToList = true;

    forkJoin([
      this.userService.saveTableFilters('tCC', merged),
      this.userService.saveTableFilters('pCC', merged),
    ]).subscribe({
      next: () => {
        this.dialogRef.close();
        this.router.navigate(['/pTickets/list']);
      },
      error: (err) => {
        console.error('Failed to apply drill-down filters:', err);
        this.isNavigatingToList = false;
      },
    });
  }

  save(): void {
    this.isSavingChanges = true;
    const payload: report = {
      ...this.report,
      rep_filter:
        this.report.rep_filter instanceof HttpParams
          ? this.report.rep_filter
          : new HttpParams({ fromObject: this.report.rep_filter as any }),
    };
    if (this.report.id == -1) {
      this.reportService.create(payload).subscribe({
        next: (createdReport) => this.dialogRef.close(createdReport),
        error: (err) => console.error('Error saving report:', err),
      });
    } else {
      this.reportService.update(payload).subscribe({
        next: (updatedReport) => {
          this.isSavingChanges = false;
          this.dialogRef.close(updatedReport);
        },
      });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  toggleEditTitle(): void {
    this.editingTitle = !this.editingTitle;
  }
}
